import { Component, inject, signal, HostListener } from "@angular/core";
import { Router, RouterModule } from "@angular/router";
import { CommonModule } from "@angular/common";
import { SidebarService } from "../../services/sidebar.service";
import { RippleModule } from "primeng/ripple";
import { TooltipModule } from "primeng/tooltip";
import { environment } from "../../../environments/environment";
import { getUserRoleFromToken } from "../../core/guards/auth-role.helper";
import { toSignal } from "@angular/core/rxjs-interop";
import { SIDEBAR_MENU } from "../../core/constants/sidebar-menu.const";
import { AppStateService } from "../../services/app-state.service";
import { DashboardSearchService } from "../../services/dashboard-search.service";

interface MenuItem {
  label: string;
  icon: string;
  route?: string;
  tooltip: string;
  roles: string[];
  children?: MenuItem[];
}

@Component({
  selector: "quote-hub-sidebar",
  standalone: true,
  imports: [RouterModule, CommonModule, RippleModule, TooltipModule],
  templateUrl: "./sidebar.component.html",
  styleUrl: "./sidebar.component.scss",
})
export class SidebarComponent {
  private router = inject(Router);
  public sidebarService = inject(SidebarService);
  private appState = inject(AppStateService);
  private searchService = inject(DashboardSearchService);

  collapsed = toSignal(this.sidebarService.collapsed$, { initialValue: false });

  // ✅ Reactive app config (logo, platform name)
  protected appConfig = this.appState.appConfig;

  role = "";
  isLargeScreen = signal(window.innerWidth > 1200);
  expandedMenus = signal<Record<string, boolean>>({});

  // ✅ use external constant
  MENU: MenuItem[] = SIDEBAR_MENU;

  constructor() {
    const token = localStorage.getItem(environment.storageKey);
    this.role = getUserRoleFromToken(token);
  }

  // ✅ filter + handle children roles also (important improvement)
  get visibleMenu(): MenuItem[] {
    return this.MENU.filter((item) => item.roles.includes(this.role)).map(
      (item) => ({
        ...item,
        children: item.children?.filter((child) =>
          child.roles.includes(this.role),
        ),
      }),
    );
  }

  @HostListener("window:resize")
  onResize() {
    this.isLargeScreen.set(window.innerWidth > 1200);
  }

  toggleSidebar(): void {
    this.sidebarService.toggleSidebar();
  }

  closeSidebarOnMobile(): void {
    if (!this.isLargeScreen()) {
      this.sidebarService.setSidebarState(false);
    }
     this.searchService.clearSearch();
  }

  toggleSubmenu(label: string): void {
    const current = this.expandedMenus();
    this.expandedMenus.set({
      ...current,
      [label]: !current[label],
    });
  }

  navigate(route?: string) {
    if (route) {
      this.router.navigate([route]);
      this.searchService.clearSearch();
      this.closeSidebarOnMobile();
    }
  }
}
